import { getLocalStorage } from 'src/functions/localStorageHandler';
import {
  CONSENT_CATEGORIES,
  firstPartyAnalyticsAllowed,
  onConsentChanged,
  onConsentDecided,
  onConsentGranted,
  onConsentModelKnown,
} from 'src/functions/consent/oneTrustConsent';

export const GA4_MEASUREMENT_ID = 'G-7M2C06HKGP';

// The tag reads its own dataLayer, not the shared one the GTM container
// reads. Third parties push gtag commands into the shared layer for Google
// tags of their own: HubSpot's ads pixel its Google Ads config and `set`
// commands, OneTrust and Hotjar their events. A GA4 tag reading that layer
// treats every command as its own and sends those events to GA4. The Consent
// Mode script pushes every consent command to both layers.
export const GA4_DATA_LAYER = 'ga4DataLayer';

// The same first-party proxy that serves the GTM container: gtg-script picks
// gtag.js for a G- id, so an ad blocker's list of Google hosts does not stop
// the script. The tag builds its collect endpoint at runtime, so its hits go to
// google-analytics.com directly, as the container's GA4 hits did.
export const GA4_TAG_URL = `/gtg/script?id=${GA4_MEASUREMENT_ID}&l=${GA4_DATA_LAYER}`;

// Google's per-tag kill switch. gtag.js cannot be unloaded, so an answer that
// withdraws Performance after the tag loaded sets it, and a later grant clears it.
export const GA4_DISABLE_FLAG = `ga-disable-${GA4_MEASUREMENT_ID}`;

// Tells the GTM container that GA4 is served by this tag: the container's
// Google tag and its `user_id` config tag carry a trigger exception on this Data
// Layer Variable, so the container never configures GA4 a second time. Its GA4
// event tags have no exception and send through this page's tag.
export const GA4_DIRECT_TAG_VARIABLE = 'ga4_direct_tag';

// Cross-domain linking, enhanced measurement and the rest of the tag's
// behavior are property settings the tag fetches from Google, not config here.
const GA4_CONFIG = {
  send_page_view: true,
  cookie_domain: 'auto',
};

// `ga4gtag` is the command function for the tag's layer, which the Consent
// Mode script defines; the Head loader injects that script ahead of OneTrust.
// gtag.js reads a command as the `arguments` object that function pushes; an
// array in its place is data, not a command. Without the script there are no
// Consent Mode defaults either, and gtag.js would then treat every storage as
// granted, so no tag is loaded.
type GtagWindow = Window & { dataLayer?: unknown[]; ga4gtag?: (...args: unknown[]) => void };

const getWindow = (): GtagWindow | null => (typeof window === 'undefined' ? null : (window as GtagWindow));

const gtag = (...args: unknown[]): boolean => {
  const w = getWindow();
  if (!w || typeof w.ga4gtag !== 'function') {
    return false;
  }
  w.ga4gtag(...args);
  return true;
};

const setDisabled = (disabled: boolean): void => {
  const w = getWindow();
  if (w) {
    (w as unknown as Record<string, unknown>)[GA4_DISABLE_FLAG] = disabled;
  }
};

const tagInjected = (): boolean => !!document.querySelector(`script[src="${GA4_TAG_URL}"]`);

// The visitor id goes through `set`, ahead of the config, because the tag
// applies `user_id` from a `set` to every later hit and ignores it on a
// `config` (observed on the live tag: a `config` carrying it produced hits
// without `uid`, a `set` produced hits with it). The Statsig provider persists
// the id on the same OneTrust signal that schedules the tag, and it subscribed
// first (at mount, before the window load the Head loader waits for), so the
// id is usually there to read; setGA4TagUserId covers an id that arrives later.
const applyUserId = (userId: string): boolean => gtag('set', { user_id: userId });

const injectTag = (): void => {
  if (tagInjected()) {
    return;
  }
  const userId = getLocalStorage('statsigUserId');
  if (userId && !applyUserId(userId)) {
    return;
  }
  if (!gtag('js', new Date())) {
    return;
  }
  gtag('config', GA4_MEASUREMENT_ID, GA4_CONFIG);

  const script = document.createElement('script');
  script.src = GA4_TAG_URL;
  script.async = true;
  document.body.appendChild(script);
};

let injectTimer: ReturnType<typeof setTimeout> | null = null;

// The Consent Mode script listens to the same OneTrust signal as this module
// and pushes the analytics_storage grant after this module's listener has run.
// gtag.js processes its layer in order, so a config pushed in the same call
// stack would send the first page view before the grant, as a cookieless ping
// with a temporary client id. One macrotask later every listener of the
// signal has pushed; the rule is read again then, in case an answer came in
// between.
const scheduleInject = (): void => {
  if (injectTimer || tagInjected()) {
    return;
  }
  injectTimer = setTimeout(() => {
    injectTimer = null;
    if (firstPartyAnalyticsAllowed()) {
      injectTag();
    }
  }, 0);
};

/**
 * Loads the GA4 tag under `firstPartyAnalyticsAllowed`: at page load outside
 * the opt-in model, otherwise on a Performance grant. Keeps the tag's disable
 * flag in step with every later answer. Returns an unsubscribe function.
 */
export const loadGA4Tag = (): (() => void) => {
  const w = getWindow();
  if (!w) {
    return () => undefined;
  }

  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ [GA4_DIRECT_TAG_VARIABLE]: true });

  const apply = (): void => {
    const allowed = firstPartyAnalyticsAllowed();
    setDisabled(!allowed);
    if (allowed) {
      scheduleInject();
    }
  };

  const { performance } = CONSENT_CATEGORIES;
  const unsubscribes = [
    // A first-time visitor outside the opt-in model: the tag loads once the
    // SDK reports the model, before any answer.
    onConsentModelKnown(apply),
    onConsentGranted(performance, apply),
    // The first answer, which may withdraw what the early tag had; then every
    // change of the Performance grant either way.
    onConsentDecided(performance, apply),
    onConsentChanged(performance, apply),
  ];

  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
};

/**
 * Applies the visitor's `user_id` to a tag already on the page, for the hits
 * that follow, when `firstPartyAnalyticsAllowed`. A tag injected later reads
 * the id itself, so nothing is queued ahead of its own `js` and `config`.
 * Returns whether it was applied.
 */
export const setGA4TagUserId = (userId: string): boolean => {
  if (!userId || !tagInjected() || !firstPartyAnalyticsAllowed()) {
    return false;
  }
  return applyUserId(userId);
};

// Test helper - clears the disable flag, a pending injection and the injected tag
export const __resetGA4TagForTesting__ = (): void => {
  const w = getWindow();
  if (!w) {
    return;
  }
  if (injectTimer) {
    clearTimeout(injectTimer);
    injectTimer = null;
  }
  delete (w as unknown as Record<string, unknown>)[GA4_DISABLE_FLAG];
  document.querySelectorAll(`script[src="${GA4_TAG_URL}"]`).forEach((script) => script.remove());
};
